import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { MessagingEntityReferenceEntityType } from '../../../api/generated/model/messagingEntityReferenceEntityType'
import type { MessagingTopic } from '../../../api/generated/model/messagingTopic'
import {
  getListMessagingConversationsQueryKey,
  useCreateMessagingConversation,
  useListMessagingConversations,
} from '../../../api/generated/messaging/messaging'
import { PexIcon } from '../../../components/icons/PexIcon'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
import { useMessagingUiStore } from '../../../store/messaging-ui'
import { useMessagingCapabilities } from './useMessagingCapabilities'

type EntityMessagingActionProps = {
  surface: 'seller' | 'supply'
  entityType: MessagingEntityReferenceEntityType
  entityId: string
  topic: MessagingTopic
}

export function EntityMessagingAction({
  surface,
  entityType,
  entityId,
  topic,
}: EntityMessagingActionProps) {
  const t = useT(messagingI18n)
  const queryClient = useQueryClient()
  const capabilities = useMessagingCapabilities()
  const openTenantDrawer = useMessagingUiStore((state) => state.openTenantDrawer)
  const [error, setError] = useState(false)
  const existing = useListMessagingConversations(
    { entity_type: entityType, entity_id: entityId, limit: 1 },
    {
      query: {
        enabled: capabilities.can_view_inbox,
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  )
  const create = useCreateMessagingConversation()
  const label =
    surface === 'seller' ? t.entityAction.order : t.entityAction.workOrder
  const isOpening = create.isPending

  if (!capabilities.can_view_inbox || !capabilities.can_send) return null

  const refreshConversationLists = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getListMessagingConversationsQueryKey({
          entity_type: entityType,
          entity_id: entityId,
          limit: 1,
        }),
      }),
      queryClient.invalidateQueries({
        queryKey: getListMessagingConversationsQueryKey(),
      }),
    ])

  const openConversation = async () => {
    setError(false)
    if (existing.isError || existing.data?.status !== 200) {
      setError(true)
      return
    }
    const current =
      existing.data.data.items[0]
    if (current) {
      await refreshConversationLists()
      openTenantDrawer(current.id)
      return
    }

    try {
      const result = await create.mutateAsync({
        data: {
          topic,
          entity_link: {
            entity_type: entityType,
            entity_id: entityId,
          },
        },
      })
      if (result.status !== 201) throw new Error('Conversation create failed')
      await refreshConversationLists()
      openTenantDrawer(result.data.id)
    } catch {
      setError(true)
    }
  }

  return (
    <div className="flex flex-col items-start gap-1.5">
      <button
        type="button"
        disabled={isOpening || existing.isLoading}
        onClick={() => void openConversation()}
        className="inline-flex min-h-10 items-center gap-2 rounded-[var(--radius-control)] border border-[var(--color-signal)] bg-[var(--color-surface)] px-3 py-2 text-sm font-semibold text-[var(--color-signal)] transition hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] disabled:cursor-wait disabled:opacity-60"
      >
        <PexIcon name="messages" size="xs" />
        {isOpening ? t.entityAction.opening : label}
      </button>
      {surface === 'seller' ? (
        <p className="max-w-xl text-xs leading-4 text-slate-500">
          {t.states.externalCustomerNotice}
        </p>
      ) : null}
      {error ? (
        <p className="text-xs font-medium text-rose-700" role="alert">
          {t.entityAction.error}
        </p>
      ) : null}
    </div>
  )
}
